import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import _ from 'lodash';
import { forkJoin, of, Subject, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CardSummarySinglelineComponent } from 'src/app/core/card-summary-singleline/card-summary-singleline.component';
import { DynamicFormComponent } from 'src/app/core/dynamic-form/dynamic-form.component';
import { DynamicFormModule } from 'src/app/core/dynamic-form/dynamic-form.module';
import { DynamicFormOptions, FieldConfig, FieldTypeEnum } from 'src/app/core/dynamic-form/dynamicForm.interface';
import { EditHistoryData } from 'src/app/core/edit-history/edit-history';
import { EditHistoryModule } from 'src/app/core/edit-history/edit-history.module';
import { customBreadCrumbRequest } from 'src/app/core/bread-crumbs/bread-crumbs.component';
import { ToastService } from 'src/app/core/toast/toast.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { environment } from 'src/environments/environment';
import { MissionDto } from '../../gestione-missioni/missioni-dto';
import { GestioneMissioniService } from '../../gestione-missioni/gestione-missioni.service';
import { CreatePoi, PoiDto, UpdatePoi } from '../poi-dto';
import { PoiService } from '../poi.service';

@Component({
  selector: 'app-form-poi',
  imports: [
    CommonModule,
    DynamicFormModule,
    EditHistoryModule,
    CardSummarySinglelineComponent,
    MatButtonModule,
    MatMenuModule,
  ],
  templateUrl: './form-poi.component.html',
  styleUrl: './form-poi.component.scss'
})
export class FormPoiComponent implements OnInit, OnDestroy {
  @ViewChild('myForm') myForm!: DynamicFormComponent;

  public object?: PoiDto;

  codice = '';
  form2Opts: DynamicFormOptions;
  ready = false;
  editHistoryData: EditHistoryData | undefined;
  regConfig2: FieldConfig[] = [];

  private theEnd$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
    private poiService: PoiService,
    private localStorageService: LocalStorageService,
    private missionService: GestioneMissioniService,
  ) {
    this.form2Opts = {
      buttonAtBottom: true,
      formType: 'add',
      editOptions: {
        entityName: 'poi',
        entityDisplayName: 'POI',
        deleteUrl: `${environment.apiUrl}/pois`,
        deleteMessage: 'Stai per eliminare il POI, procedere?',
        deleteItem: '',
        entityId: '',
        isFem: false,
        redirectUrl: 'home/poi',
      },
      addOptions: {
        entityName: 'poi',
        entityDisplayName: 'POI',
        isFem: false,
        redirectUrl: 'home/poi',
      },
    };
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.theEnd$.next();
    this.theEnd$.complete();
  }

  initializeForm(): void {
    if (this.router.url.includes('new')) {
      this.form2Opts.formType = 'add';
      this.localStorageService.setTitolo('Nuovo POI');
      this.initializeFormFields();
      return;
    }

    this.form2Opts.formType = 'edit';
    this.form2Opts.buttonAtBottom = false;
    this.initializeEditForm();
  }

  initializeFormFields(): void {
    this.regConfig2 = [
      {
        type: FieldTypeEnum.input,
        name: 'name',
        label: 'Nome',
        isRequired: true,
        width: 'w-full',
        value: this.object?.name,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Inserisci un nome',
          },
        ],
      },
      {
        type: FieldTypeEnum.textarea,
        name: 'shortDescription',
        label: 'Breve descrizione',
        width: 'w-full',
        value: this.object?.shortDescription ?? '',
      },
      {
        type: FieldTypeEnum.textarea,
        name: 'description',
        label: 'Descrizione',
        width: 'w-full',
        value: this.object?.description ?? '',
      },
      {
        type: FieldTypeEnum.input,
        name: 'latitude',
        label: 'Latitudine',
        isRequired: true,
        width: 'w-1/2',
        value: this.object?.latitudine ?? 0,
        inputType: 'text',
        placeholder: 'Es. 45.1335 oppure 45,1335',
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Inserisci la latitudine',
          },
        ],
      },
      {
        type: FieldTypeEnum.input,
        name: 'longitude',
        label: 'Longitudine',
        isRequired: true,
        width: 'w-1/2',
        value: this.object?.longitudine ?? 0,
        inputType: 'text',
        placeholder: 'Es. 9.1234 oppure 9,1234',
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Inserisci la longitudine',
          },
        ],
      },
      {
        type: FieldTypeEnum.autoComplete,
        name: 'poiTypeName',
        label: 'Tipo',
        width: 'w-full',
        value: this.object?.poiType?.name ?? this.object?.poiTypeName ?? null,
        placeholder: 'Seleziona o scrivi un tipo',
        allowAdd: true,
        typeAheadOptionField: 'codice',
        options: {
          api: true,
          url: '-pois/types',
        },
      },
      {
        type: FieldTypeEnum.autoComplete,
        name: 'poiLocationName',
        label: 'Location',
        width: 'w-full',
        value: this.object?.poiLocation?.name ?? this.object?.poiLocationName ?? null,
        placeholder: 'Seleziona o scrivi una location',
        allowAdd: true,
        typeAheadOptionField: 'codice',
        options: {
          api: true,
          url: '-pois/locations',
        },
      },
    ];

    this.ready = true;
  }

  initializeEditForm(): void {
    this.codice = this.route.snapshot.paramMap.get('id') || '';

    this.poiService.read(this.codice).pipe(takeUntil(this.theEnd$)).subscribe({
      next: (response: PoiDto) => {
        this.enrichPoiMissions(response);
      },
      error: () => {
        this.toast.error('Errore durante il caricamento del POI');
      },
    });
  }

  submit(event: any): void {
    if (this.form2Opts.formType === 'add') {
      this.add(event);
      return;
    }

    this.edit(event);
  }

  edit(event: any): void {
    const latitudine = this.parseDecimal(event.latitude);
    const longitudine = this.parseDecimal(event.longitude);

    if (latitudine === null || longitudine === null) {
      this.toast.error('Latitudine e longitudine devono essere numeri validi');
      return;
    }

    const data: UpdatePoi = {
      name: event.name,
      shortDescription: event.shortDescription ?? '',
      description: event.description ?? '',
      unlockType: 'immediate',
      latitudine,
      longitudine,
      radius: null,
      isActive: true,
      poiTypeName: this.normalizeSelectValue(event.poiTypeName),
      poiLocationName: this.normalizeSelectValue(event.poiLocationName),
    };

    this.poiService.update(this.codice, data).pipe(takeUntil(this.theEnd$)).subscribe({
      next: () => {
        this.toast.success('Modifiche salvate');
        this.initializeForm();
      },
      error: () => {
        this.toast.error('Errore durante il salvataggio del POI');
      },
    });
  }

  add(event: any): void {
    const latitudine = this.parseDecimal(event.latitude);
    const longitudine = this.parseDecimal(event.longitude);

    if (latitudine === null || longitudine === null) {
      this.toast.error('Latitudine e longitudine devono essere numeri validi');
      return;
    }

    const data: CreatePoi = {
      name: event.name,
      shortDescription: event.shortDescription ?? '',
      description: event.description ?? '',
      unlockType: 'immediate',
      latitudine,
      longitudine,
      radius: null,
      isActive: true,
      poiTypeName: this.normalizeSelectValue(event.poiTypeName),
      poiLocationName: this.normalizeSelectValue(event.poiLocationName),
    };

    this.poiService.create(data).pipe(takeUntil(this.theEnd$)).subscribe({
      next: (response: PoiDto) => {
        this.toast.success('POI creato');
        this.router.navigate(['home/poi', response.id]);
      },
      error: () => {
        this.toast.error('Errore durante la creazione del POI');
      },
    });
  }

  goBackToPois(): void {
    this.router.navigate(['home/poi']);
  }

  editMission(mission: MissionDto): void {
    const baseRoute = mission.isSecondary ? 'home/missioni-esplorative' : 'home/missioni';
    this.router.navigate([baseRoute, mission.id]);
  }

  deleteMission(id: string): void {
    this.missionService.delete(id).pipe(takeUntil(this.theEnd$)).subscribe({
      next: () => {
        this.toast.success('Missione eliminata');
        this.initializeEditForm();
      },
      error: () => {
        this.toast.error("Errore durante l'eliminazione della missione");
      },
    });
  }

  unlockTypeToString(ut?: string): string {
    switch (ut) {
      case 'gps': return 'GPS';
      case 'immediate': return 'Immediato';
      case 'time': return 'Tempo';
      case 'qr': return 'QR Code';
      default: return 'Immediato';
    }
  }

  private enrichPoiMissions(response: PoiDto): void {
    const missions = response.missions ?? [];

    if (missions.length === 0) {
      this.applyPoiResponse(response);
      return;
    }

    forkJoin(
      missions.map((mission) =>
        mission.id
          ? this.missionService.read(mission.id).pipe(
              catchError(() => of(mission)),
              map((fullMission) => ({
                ...mission,
                ...fullMission,
              }))
            )
          : of(mission)
      )
    )
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (fullMissions) => {
          this.applyPoiResponse({
            ...response,
            missions: fullMissions,
          });
        },
        error: () => {
          this.applyPoiResponse(response);
        },
      });
  }

  private applyPoiResponse(response: PoiDto): void {
    this.object = _.cloneDeep(response);
    this.localStorageService.setTitolo(`Modifica POI ${response.name}`);

    const req: customBreadCrumbRequest = {
      oldString: response.id,
      newString: response.name,
    };
    this.localStorageService.setCustomBreadCrumb(req);

    if (this.form2Opts.editOptions) {
      this.form2Opts.editOptions.deleteItem = this.object.id;
      this.form2Opts.editOptions.entityId = this.object.id;
    }

    this.initializeFormFields();
  }

  private parseDecimal(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const normalized = value.trim().replace(',', '.');
    const parsedValue = Number(normalized);

    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private normalizeSelectValue(value: string | number | null | undefined): string | null {
    const normalized = value?.toString().trim();
    return normalized ? normalized.toUpperCase() : null;
  }
}
